/**
 * Atop a space's pages: where the org stands with its plan when it's news (its trial's last days, a failed payment,
 * read-only, suspended) and the limits it's near or at (see `planNotices`); admins get a way to the plan page.
 */
import type { SpaceOut, Standing } from "../../api/types.gen.ts";
import { type Key, t } from "../../i18n/index.ts";
import { sold } from "../../lib/billing.ts";
import { formatBytes, formatInstant, formatRelative } from "../../lib/format.ts";
import { spaceUrl } from "../../lib/paths.ts";
import { atLeast } from "../../lib/roles.ts";
import { navigate } from "../../lib/router.ts";
import { type PlanNotice, planNotices } from "../../lib/standing.ts";
import { PlanAlertIcon } from "../../ui/icons.tsx";
import { Banner } from "../../ui/misc.tsx";

const DATE: Intl.DateTimeFormatOptions = { dateStyle: "long" };

/** Each notice's message: to admins, and to everyone else (none: admins only). */
const MESSAGES: Record<PlanNotice["kind"], [Key, Key | null]> = {
  trial: ["plan.trial", null],
  pastDue: ["plan.pastDue", null],
  grace: ["plan.grace", "plan.graceMember"],
  suspended: ["plan.suspended", "plan.suspendedMember"],
  storageNear: ["plan.storageNear", null],
  storageFull: ["plan.storageFull", "plan.storageFullMember"],
  emailsUsed: ["plan.emailsUsed", "plan.emailsUsed"],
};

/** In the iOS app (plans aren't sold there: `sold`): where the org stands, without a word about buying one. */
const APP_MESSAGES: Record<PlanNotice["kind"], [Key, Key | null]> = {
  trial: ["plan.app.trial", null],
  pastDue: ["plan.app.pastDue", null],
  grace: ["plan.app.grace", "plan.app.graceMember"],
  suspended: ["plan.app.suspended", "plan.app.suspendedMember"],
  storageNear: ["plan.app.storageNear", null],
  storageFull: ["plan.app.storageFull", "plan.storageFullMember"],
  emailsUsed: ["plan.emailsUsed", "plan.emailsUsed"],
};

/** What a notice says, to an admin or to anyone else. */
function message(n: PlanNotice, st: Standing, admin: boolean): string {
  const [forAdmins, forOthers] = (sold() ? MESSAGES : APP_MESSAGES)[n.kind];
  const date = (iso: string | null | undefined) => (iso ? formatInstant(iso, DATE) : "");
  return t((admin ? forAdmins : forOthers) ?? forAdmins, {
    count: n.count ?? 0,
    ended: date(st.ended_at),
    until: date(st.mail_until),
    kept: date(st.kept_until),
    limit: formatBytes(st.limits?.storage ?? 0),
    emails: st.limits?.emails ?? 0,
    in: st.usage.emails_room_at ? formatRelative(st.usage.emails_room_at) : "",
  });
}

/** The banners, for whoever's looking (admins: with a button to the plan page, unless they're `here` on it). */
export function PlanBanner({ space, here = false }: { space: SpaceOut; here?: boolean }) {
  const admin = atLeast(space.role, "admin");
  const toPlan = () => navigate(spaceUrl(space, "/settings/billing"));
  const button = (n: PlanNotice) =>
    admin && !here
      ? { label: t(n.kind === "trial" && sold() ? "plan.choose" : "billing.see"), onClick: toPlan }
      : undefined;
  return (
    <>
      {planNotices(space.standing, admin).map((n) => (
        <Banner key={n.kind} tone={n.tone} icon={<PlanAlertIcon size={18} />} action={button(n)}>
          <p>{message(n, space.standing, admin)}</p>
        </Banner>
      ))}
    </>
  );
}
